import React, { useContext, TouchEvent, MouseEvent } from 'react';
import HeaderNavBarContext from './context/HeaderNavBarContext';
import fireEvent from './utils/fireEvent';
import getFormattedMenuLinks from './utils/getFormattedMenuLinks';
import useTarget from './hooks/useTarget';
import Box from '../../components/Box/Box';
import { TMenuLink } from '../../types/TMenuLink';
import LoginButton from './utils/LoginButton';
import { TTranslations } from '../../types/THeaderConfig';
import { HamburgerMenuOverlay, CloseHamburgerMenu } from './HamburgerMenuVisibility';

type THamburgerMenu = {
  isVisible: boolean;
  hideHamburgerMenu: () => void;
  menuLinks: TMenuLink[];
  className?: string;
  translations?: TTranslations;
};

type TEvent = TouchEvent<HTMLButtonElement | HTMLAnchorElement> | MouseEvent<HTMLButtonElement | HTMLAnchorElement>;

type THamburgerMenuHeader = {
  menuTitle?: string;
  hideHamburgerMenu: () => void;
};

const handlePlaceAd = (event: TEvent) => {
  const placeAdEvent = fireEvent(
    'placead',
    {},
    {
      cancelable: true,
      node: event.target as HTMLElement,
    },
  );

  if (placeAdEvent.defaultPrevented) {
    event.preventDefault();
  }
};

const onTouchStart = (e: TEvent) => {
  const menu = e.target as HTMLElement;
  // prevent scroll-through if menu scrolled to the same top or bottom
  if (menu.scrollTop === 0) {
    menu.scrollTop = 1;
  } else if (menu.scrollTop + menu.offsetHeight === menu.scrollHeight) {
    menu.scrollTop -= 1;
  }
};

const onTouchMove = (e: TEvent) => {
  const menu = e.target as HTMLElement;
  if (menu.scrollTop === 0) {
    e.preventDefault();
  }
};

function HamburgerMenuHeader({ menuTitle = '', hideHamburgerMenu }: THamburgerMenuHeader) {
  return (
    <Box as="li" parent="Nav" name="pill" modifiers="header">
      {menuTitle}
      <CloseHamburgerMenu hideHamburgerMenu={hideHamburgerMenu} />
    </Box>
  );
}

const HamburgerMenu = ({ isVisible, hideHamburgerMenu, menuLinks, translations }: THamburgerMenu) => {
  const { userDetails, userLinks, headerLinks, ssoLinks } = useContext(HeaderNavBarContext);
  const loginTarget = useTarget('');

  if (!(isVisible && userLinks)) return null;

  // TODO: fix this!
  const { isLoggedIn } = userDetails!;

  return (
    <>
      <HamburgerMenuOverlay isVisible={isVisible} hideHamburgerMenu={hideHamburgerMenu} />

      <Box
        as="div"
        name="HamburgerMenu"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        data-role="hamburgerMenu"
      >
        <Box as="ul" parent="Nav" name="pills">
          <HamburgerMenuHeader menuTitle={translations?.menuTitle} hideHamburgerMenu={hideHamburgerMenu} />

          {userLinks.placeAd && (
            <>
              <li>
                <a href={userLinks.placeAd.url} onClick={handlePlaceAd} data-role={userLinks.placeAd.role} tabIndex={0}>
                  {userLinks.placeAd.text}
                </a>
              </li>
              <Box as="li" parent="Nav" name="pill" modifiers="separator" />
            </>
          )}

          {Boolean(isLoggedIn && userLinks.shoppingCart) && (
            <li>
              <a href={userLinks.shoppingCart.url} data-role={userLinks.shoppingCart.role} tabIndex={0}>
                {userLinks.shoppingCart.text}
              </a>
            </li>
          )}

          {userLinks.messages && (
            <li>
              <a href={userLinks.messages.url} data-role={userLinks.messages.role} tabIndex={0}>
                {userLinks.messages.text}
                {Boolean(userLinks.messages.badge.count) && <span>({userLinks.messages.badge.count})</span>}
              </a>
            </li>
          )}

          {isLoggedIn && getFormattedMenuLinks(menuLinks).map(({ label, value }) => <li key={value}>{label}</li>)}

          <Box as="li" parent="Nav" name="pill" modifiers="separator" />

          {headerLinks?.map(({ text, url, target = '_blank' }) => (
            <li key={text}>
              <a href={url} target={target} tabIndex={0}>
                {text}
              </a>
            </li>
          ))}

          <Box as="li" parent="Nav" name="pill" modifiers="separator" />

          {userLinks.privacy && (
            <li key={'Privacyvoorkeuren'}>
              <a href={userLinks.privacy.url} target={'_blank'} tabIndex={0}>
                {userLinks.privacy.text}
              </a>
            </li>
          )}

          <LoginButton isLoggedIn={Boolean(isLoggedIn)} ssoLinks={ssoLinks} loginTarget={loginTarget} />

          {userLinks.localeAlt && (
            <>
              <Box as="li" parent="Nav" name="pill" modifiers="separator" />
              <li>
                <a href={userLinks.localeAlt.url} data-role={userLinks.localeAlt.role}>
                  {userLinks.localeAlt.textLong}
                </a>
              </li>
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

export default HamburgerMenu;
